import React, { useState, useCallback } from 'react';
import { trackClick } from 'utility/analytics';
import { LG, MD, SM } from '@zendeskgarden/react-typography';
import Flex from 'styled-flex-component';
import { Grid, Row, Col } from '@zendeskgarden/react-grid';
import { Button } from 'theme/Button';
import InPageOverlay from 'components/InPageOverlay/InPageOverlay';
import ProductsAboutPipeFacts from 'routes/Products/components/ProductsAboutPipeFacts/ProductsAboutPipeFacts';
import { PropTypes } from 'prop-types';
import { productNominalDetailsShape } from 'propTypesObjects';
import { webRefactorSwitch } from 'utility/environment';
import { map } from 'lodash';
import { openModal } from 'state/modals/actions';
import {
  AttributeRow,
  TitleWrapper,
  LeftColumn,
  RightColumn,
  CtaHeader,
  Attribute,
  AttributeWrapper,
  HeroWrapper,
  HeroTitle,
  HeroDescription,
  TechnicalOverviewWrapper,
  AttributeBox,
} from './ProductsHero.styles';

function ProductsHero({ productDetails, onAddToWishlist, onInquire, userId }) {
  const [inPageOverlayViewOpen, setInPageOverlay] = useState(false);
  const title = productDetails.name || '';
  const description = productDetails.description || '';
  const attributes = productDetails.criteria;

  return webRefactorSwitch ? (
    <div>
      <ProductHeroCard title={title} description={description} />
      <TechnicalOverviewBar attributes={attributes} />
    </div>
  ) : (
    <Grid>
      <Row>
        <Col>
          <TitleWrapper>
            <LG
              paddingBottomLg
              bold
              style={{ lineHeight: '40px', fontSize: '30px' }}
              as="h1"
            >
              {title}
            </LG>
          </TitleWrapper>
        </Col>
      </Row>
      <Row justifyContent="between">
        <Left description={description} title={title} attributes={attributes} />
        <Right
          userId={userId}
          onAddToWishlist={onAddToWishlist}
          onInquire={onInquire}
          setInPageOverlay={setInPageOverlay}
          description={description}
        />
      </Row>

      <InPageOverlay
        showClose
        onClose={() => setInPageOverlay(false)}
        open={inPageOverlayViewOpen}
      >
        <div style={{ padding: '30px' }}>
          <ProductsAboutPipeFacts />
        </div>
      </InPageOverlay>
    </Grid>
  );
}

ProductsHero.propTypes = {
  productDetails: productNominalDetailsShape,
  onAddToWishlist: PropTypes.func,
  onInquire: PropTypes.func,
  userId: PropTypes.number,
};

function Left({ attributes, title, description } = {}) {
  // description = 'This is a description of the page in question, for an item on the product page when we need to show more information on a product we do so here and we might need to have a link that shoes more details.'
  return (
    <LeftColumn>
      <TitleWrapper inline>
        <LG
          bold
          paddingBottomMd
          style={{ lineHeight: '40px', fontSize: '30px', marginBottom: 0 }}
          as="h1"
        >
          {title}
        </LG>
      </TitleWrapper>
      {description ? (
        <SM style={{ maxWidth: '700px' }} grey paddingBottomMd as="h3">
          {description}
        </SM>
      ) : null}
      <HeroAttributes description={description} attributes={attributes} />
    </LeftColumn>
  );
}

Left.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  attributes: PropTypes.array,
  title: PropTypes.string,
  description: PropTypes.string,
};

function Right({ setInPageOverlay, onAddToWishlist, onInquire, userId }) {
  const [addingToWishlist, setAddingToWishist] = useState(false);
  const [addToWishListState, setAddToWishListState] = useState({});

  const addToWishlist = useCallback(async () => {
    setAddingToWishist(!!userId);
    trackClick('addProductToWishlist', 'Add To Wishlist', 'Product');
    const added = await onAddToWishlist();
    if (added && added.error) {
      setAddToWishListState({ error: added.error });
    } else {
      setAddToWishListState({ success: true });
    }
    setAddingToWishist(false);
  }, [onAddToWishlist, userId]);

  const inquire = useCallback(() => {
    trackClick('inquireProduct', 'Request Availability & Pricing', 'Product');
    onInquire();
  }, [onInquire]);

  return (
    <RightColumn size={12} md="100%">
      <Flex column>
        <CtaHeader>
          <img
            alt="Pipe Facts"
            style={{ marginTop: '6px', maxWidth: '100px' }}
            src="/images/PIPEFACTS-HORIZONTAL.png"
          />
          <SM paddingTopSm as="h3" grey>
            PipeFacts<sup>TM</sup> is a comprehensive inspection and
            certification process to ensure quality and reliability.
            <SM as="span" onClick={() => setInPageOverlay(true)} link>
              Learn More
            </SM>
          </SM>
        </CtaHeader>
        <div>
          <Button
            onClick={inquire}
            style={{ width: '100%', maxWidth: '100%', fontSize: '16px' }}
            size="large"
            spacingTop
            primary
            bold
          >
            Request Availability & Pricing
          </Button>
        </div>
        <div>
          <Button
            error={addToWishListState.error}
            loading={addingToWishlist}
            buttonStatus
            onClick={addToWishlist}
            style={{ width: '100%', maxWidth: '100%' }}
            size="large"
            spacingTop
            primaryOutline
            bold
          >
            Add To Wishlist
          </Button>
        </div>
        {addToWishListState.error ? (
          <SM paddingTopSm error>
            {addToWishListState.error}
          </SM>
        ) : null}
        {false ? (
          <SM
            grey
            style={{
              fontSize: '13px',
              textAlign: 'center',
              margin: '20px auto 0',
              color: 'rgb(156, 156, 156)',
            }}
          >
            Need Engineering Help?{' '}
            <Button link blue style={{ width: '63px' }} size="large" bold>
              Click here
            </Button>
          </SM>
        ) : null}
      </Flex>
    </RightColumn>
  );
}

Right.propTypes = {
  setInPageOverlay: PropTypes.func,
  onAddToWishlist: PropTypes.func,
  onInquire: PropTypes.func,
  userId: PropTypes.number,
};

function HeroAttributes({ description, attributes } = {}) {
  return (
    <div>
      {description ? (
        <MD bold noMargin as="h3">
          Technical Overview
        </MD>
      ) : null}
      <AttributeRow description={!!description}>
        <AttributeWrapper size="auto">
          <Attribute>
            <SM style={{ fontSize: '14px' }} noMargin as="h3" bold>
              OD
            </SM>
            <SM style={{ fontSize: '14px' }} paddingTopSm noMargin as="h3">
              {`${attributes.od}"` || ''}
            </SM>
          </Attribute>
        </AttributeWrapper>
        <AttributeWrapper size="auto">
          <Attribute>
            <SM style={{ fontSize: '14px' }} noMargin as="h3" bold>
              #/FT
            </SM>
            <SM style={{ fontSize: '14px' }} paddingTopSm noMargin as="h3">
              {`${attributes.weight_per_foot} LB` || ''}
            </SM>
          </Attribute>
        </AttributeWrapper>
        <AttributeWrapper size="auto">
          <Attribute>
            <SM style={{ fontSize: '14px' }} noMargin as="h3" bold>
              Wall
            </SM>
            <SM style={{ fontSize: '14px' }} paddingTopSm noMargin as="h3">
              {`${attributes.wall}"` || ''}
            </SM>
          </Attribute>
        </AttributeWrapper>
        <AttributeWrapper size="auto">
          <Attribute>
            <SM style={{ fontSize: '14px' }} noMargin as="h3" bold>
              ID
            </SM>
            <SM style={{ fontSize: '14px' }} paddingTopSm noMargin as="h3">
              {`${attributes.inside_diameter}"` || ''}
            </SM>
          </Attribute>
        </AttributeWrapper>
        <AttributeWrapper size="auto">
          <Attribute>
            <SM style={{ fontSize: '14px' }} noMargin as="h3" bold>
              Min Yield
            </SM>
            <SM style={{ fontSize: '14px' }} paddingTopSm noMargin as="h3">
              {`${attributes.min_yield} KSI` || ''}
            </SM>
          </Attribute>
        </AttributeWrapper>
        <AttributeWrapper size="auto">
          <Attribute>
            <SM style={{ fontSize: '14px' }} noMargin as="h3" bold>
              Drift
            </SM>
            <SM style={{ fontSize: '14px' }} paddingTopSm noMargin as="h3">
              {`${attributes.drift}"` || ''}
            </SM>
          </Attribute>
        </AttributeWrapper>
      </AttributeRow>
    </div>
  );
}

HeroAttributes.propTypes = {
  description: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  attributes: PropTypes.array,
};

function ProductHeroCard({ title, description }) {
  return (
    <HeroWrapper>
      <Grid className="product-hero-grid">
        <Col xl={9} lg={9} md={9} sm={12} className="detailsColumn">
          <HeroTitle>{title}</HeroTitle>
          <HeroDescription>{description}</HeroDescription>
        </Col>
        <Col xl={2} lg={3} md={3} sm={12} className="buttonColumn">
          <Button
            className="inquiryButton"
            onClick={(e) => {
              e.preventDefault();
              openModal({
                type: 'quickInqueryModalForReal',
              });
            }}
            style={{ fontSize: '16px' }}
            size="large"
            spacingTop
            primary
            bold
          >
            Submit an Inquiry
          </Button>
        </Col>
      </Grid>
    </HeroWrapper>
  );
}

ProductHeroCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
};

function TechnicalOverviewBar({ attributes }) {
  const {
    od,
    weight_per_foot: weightPerFoot,
    wall,
    inside_diameter: insideDiameter,
    min_yield: minYield,
    drift,
  } = attributes;

  const attributeList = [
    { name: 'Outside Diameter', value: od },
    { name: 'Weight per Foot', value: weightPerFoot },
    { name: 'Wall Thickness', value: wall },
    { name: 'Inside Diameter', value: insideDiameter },
    { name: 'Minimum Yield', value: minYield },
    { name: 'Drift', value: drift },
  ];

  function returnUom(currentAttr) {
    if (currentAttr.name === 'Weight per Foot') {
      return ' LBS';
    }
    if (currentAttr.name === 'Minimum Yield') {
      return ' KSI';
    }
    return '"';
  }

  return (
    <TechnicalOverviewWrapper>
      <div id="technicalOverviewTitle">Technical Overview</div>
      <div id="technicalAttributes">
        {map(attributeList, (attr) => (
          <AttributeBox key={attr.name}>
            <div>{attr.name}</div>
            <div className="attributeValue">{`${
              attr?.value || 'N/A'
            }${returnUom(attr)}`}</div>
          </AttributeBox>
        ))}
      </div>
    </TechnicalOverviewWrapper>
  );
}

TechnicalOverviewBar.propTypes = {
  attributes: PropTypes.shape({
    od: PropTypes.number,
    weight_per_foot: PropTypes.number,
    wall: PropTypes.number,
    inside_diameter: PropTypes.number,
    min_yield: PropTypes.number,
    drift: PropTypes.number,
  }),
};

export default ProductsHero;
